/**
 * What this workspace has built with Flow and rule segments — slice 71.
 *
 * ## WHY IT IS ON THE TENANT PAGE AT ALL
 *
 * Granting Flow is the start of a support conversation, not the end of one:
 * "Flow is on and nothing is launched" and "two flows live, one paused" need
 * different calls. Before slice 71 the only way staff could tell was to ask
 * the customer, or to open a database console — the unattributable look that
 * slice 8's boundary exists to remove the excuse for. So the page shows the
 * one thing staff need: **how many**, never *which*.
 *
 * ## COUNTS ONLY, AND SAID SO ON SCREEN
 *
 * `GET /admin/tenants/:tenantId/usage` returns flows by status and the number
 * of rule segments, and nothing else (`tenantUsageResponseSchema` is strict;
 * the API's `tenant-usage.service.ts` argues why that stays inside the
 * boundary). The panel says that in its description, because the boundary
 * panel below lists Segments among the data no admin route reaches, and a
 * count of them a few inches above with no explanation would read as the
 * boundary having a hole.
 *
 * ## ZERO, ABSENT AND "NOT ON THIS PLAN"
 *
 * A failed read is an error with a retry, never a row of zeros. A read that
 * succeeded with zeros says so — those are measured. A workspace without Flow
 * on its plan still gets its counts (a plan can be narrowed after flows were
 * built, and the flows still exist), with one line saying Flow is not on the
 * plan so a "2 live" there reads as the anomaly it is.
 */
import type { Tenant, TenantUsageResponse } from '@wend/shared';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, Spinner } from '@wend/ui';

import { ErrorState } from '../../components/ErrorState';
import { formatCount, formatDateTime } from '../../lib/format';
import { getTenantUsage } from '../../lib/tenants-api';
import { useAsyncData } from '../../lib/use-async-data';

export function TenantUsagePanel({ tenant }: { tenant: Tenant }) {
  const { data, error, status, refreshing, reload } = useAsyncData(
    (signal) => getTenantUsage(tenant.id, signal),
    [tenant.id],
  );

  return (
    <Card data-role="tenant-usage">
      <CardHeader>
        <CardTitle className="text-[1.02rem]">Flows and rule segments</CardTitle>
        <CardDescription>
          How many this workspace has built, counted when you opened this page. Counts only — no
          names, no rules, no people. Staff never see what a flow says or who is in a segment.
        </CardDescription>
        {!tenant.features.includes('flow') && (
          <p className="text-[0.85rem] leading-snug text-muted">Flow is not on this plan.</p>
        )}
      </CardHeader>

      <CardContent>
        {data === null && status === 'loading' && (
          <div className="flex items-center gap-3 py-4 text-[0.92rem] text-muted">
            <Spinner size="md" label={null} />
            <span role="status">Counting…</span>
          </div>
        )}
        {data === null && error !== null && (
          <ErrorState error={error} title="Could not count them" onRetry={reload} retrying={refreshing} />
        )}
        {data !== null && <UsageFigures usage={data} />}
      </CardContent>
    </Card>
  );
}

/** One figure per status, in the order a support call asks about them. */
export function UsageFigures({ usage }: { usage: TenantUsageResponse }) {
  const rows: readonly { readonly label: string; readonly value: number }[] = [
    { label: 'Live flows', value: usage.flows.live },
    { label: 'Paused flows', value: usage.flows.paused },
    { label: 'Draft flows', value: usage.flows.draft },
    { label: 'Archived flows', value: usage.flows.archived },
    { label: 'Rule segments', value: usage.ruleSegments },
  ];

  return (
    <div className="flex flex-col gap-3">
      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        {rows.map((row) => (
          <div key={row.label} className="flex flex-col gap-1 rounded-base border border-line px-3 py-2.5">
            <dt className="font-mono text-[0.66rem] uppercase tracking-[0.12em] text-muted">{row.label}</dt>
            <dd className="text-[1.25rem] font-semibold tabular-nums text-text">{formatCount(row.value)}</dd>
          </div>
        ))}
      </dl>
      <p className="text-[0.8rem] text-muted">Counted {formatDateTime(usage.generatedAt)}.</p>
    </div>
  );
}
